import { useCallback, useEffect, useRef, useState } from 'react';
import type { OutputOf } from '@going-once/client';
import { api } from '../lib/api';
import { refreshSession } from '../lib/auth';
import { elementsAppearance, getStripe, stripeConfigured } from '../lib/stripe';
import { describeError, useAsync } from '../lib/useAsync';
import { Card, Pressable, Sheet, Stack, Text } from '../ui/primitives';
import { ErrorNote, Loading } from '../ui/state';

type PaymentMethod = OutputOf<'orders.paymentmethod.list'>['paymentMethods'][number];

/**
 * The buyer's saved cards — and the screen that makes D44's bid gate honest.
 *
 * The gate is fail-closed: the winner of an auction is charged automatically, so a bidder
 * with no card is a sale that cannot complete and the engine refuses the bid rather than
 * finding out at settlement. Until this screen existed the fact arrived from
 * `npm run allow-bidding`, run by hand.
 *
 * **The card is never typed into this page.** The Payment Element below is an iframe
 * served by Stripe; the number goes straight from it to Stripe against a SetupIntent's
 * client secret, and what comes back to us is a webhook naming a payment method that
 * already exists. There is no state in this component that could hold a PAN.
 */
export function Cards() {
  const cards = useAsync(() => api().query('orders.paymentmethod.list', {}), []);
  const [adding, setAdding] = useState(false);
  const [removing, setRemoving] = useState<string | undefined>();
  const [problem, setProblem] = useState<{ message: string } | undefined>();

  async function remove(card: PaymentMethod) {
    setRemoving(card.paymentMethodId);
    setProblem(undefined);
    try {
      await api().command('orders.paymentmethod.remove', { paymentMethodId: card.paymentMethodId }, {});
      cards.reload();
    } catch (error) {
      setProblem(describeError(error));
    } finally {
      setRemoving(undefined);
    }
  }

  return (
    <Stack gap="lg">
      <Stack gap="xs">
        <Text as="h2" step="heading">
          Payment
        </Text>
        <Text step="label" tone="fg.muted">
          A card on file is what lets you bid — we charge it automatically when you win.
        </Text>
      </Stack>

      {cards.error ? <ErrorNote error={cards.error} onRetry={cards.reload} /> : null}
      {cards.loading && !cards.data ? <Loading what="your cards" /> : null}
      {problem ? <Text tone="danger">{problem.message}</Text> : null}

      {cards.data && cards.data.paymentMethods.length === 0 ? (
        <Card>
          <Stack gap="sm">
            <Text>No card saved yet.</Text>
            {/* Named plainly, because a refused bid with no explanation is the failure. */}
            <Text step="label" tone="fg.muted">
              You will not be able to bid until you add one.
            </Text>
          </Stack>
        </Card>
      ) : null}

      {cards.data?.paymentMethods.map((card) => (
        <Card key={card.paymentMethodId} padding="sm">
          <Stack direction="row" justify="between" align="center" wrap gap="sm">
            <Stack gap="xs">
              <Text>
                {card.brand} ···· {card.last4}
              </Text>
              <Text step="label" tone="fg.muted">
                Expires {String(card.expMonth).padStart(2, '0')}/{card.expYear}
                {card.isDefault ? ' · Default' : ''}
              </Text>
            </Stack>
            <Pressable variant="secondary" busy={removing === card.paymentMethodId} onPress={() => void remove(card)}>
              Remove
            </Pressable>
          </Stack>
        </Card>
      ))}

      {stripeConfigured() ? (
        <Pressable onPress={() => setAdding(true)}>Add a card</Pressable>
      ) : (
        <Card>
          <Text step="label" tone="fg.muted">
            Card setup is not configured in this environment — its Stripe publishable key has not been set.
          </Text>
        </Card>
      )}

      {adding ? (
        <AddCardSheet
          onDismiss={() => setAdding(false)}
          onSaved={() => {
            setAdding(false);
            cards.reload();
          }}
        />
      ) : null}
    </Stack>
  );
}

/**
 * The Payment Element, mounted imperatively.
 *
 * **No React wrapper.** `@stripe/react-stripe-js` is a context provider and a hook around
 * two calls — `elements.create('payment')` and `.mount(node)` — and this app has already
 * made the same trade twice, for the router and for the AppSync socket. The lifecycle
 * here is small enough to own: create on mount, destroy on unmount, and nothing else.
 */
function AddCardSheet({ onDismiss, onSaved }: { onDismiss: () => void; onSaved: () => void }) {
  const mountPoint = useRef<HTMLDivElement | null>(null);
  const [state, setState] = useState<'preparing' | 'ready' | 'saving' | 'confirming'>('preparing');
  const [problem, setProblem] = useState<{ message: string } | undefined>();
  const submit = useRef<(() => Promise<void>) | undefined>(undefined);

  const prepare = useCallback(async (node: HTMLDivElement) => {
    /**
     * A fresh key per sheet-open. `orders.paymentmethod.setup` declares `keyed`
     * idempotency because it opens a SetupIntent per call and is not naturally
     * idempotent — so a double-tap on "Add a card" returns the same intent rather than
     * littering the customer with a second one.
     */
    const idempotencyKey = crypto.randomUUID();
    const { clientSecret } = await api().command('orders.paymentmethod.setup', {}, { idempotencyKey });

    const stripe = await getStripe();
    const elements = stripe.elements({ clientSecret, appearance: elementsAppearance() });
    elements.create('payment').mount(node);

    submit.current = async () => {
      const { error } = await stripe.confirmSetup({
        elements,
        // Only leaves the page when the card demands it — a 3-D Secure challenge. Most
        // cards resolve in place, and a redirect for all of them would lose the sheet.
        redirect: 'if_required',
        confirmParams: { return_url: `${window.location.origin}/cards` },
      });
      if (error) throw new Error(error.message ?? 'That card could not be saved.');
    };
    return () => elements.getElement('payment')?.destroy();
  }, []);

  useEffect(() => {
    const node = mountPoint.current;
    if (!node) return;
    let cleanup: (() => void) | undefined;
    let cancelled = false;

    prepare(node)
      .then((dispose) => {
        // React mounts effects twice in development; if we were unmounted while the
        // SetupIntent was in flight, tear the element down instead of leaving one
        // attached to a node nobody is looking at.
        if (cancelled) dispose();
        else {
          cleanup = dispose;
          setState('ready');
        }
      })
      .catch((error: unknown) => {
        if (!cancelled) setProblem(describeError(error));
      });

    return () => {
      cancelled = true;
      cleanup?.();
    };
  }, [prepare]);

  async function save() {
    if (!submit.current) return;
    setState('saving');
    setProblem(undefined);
    try {
      await submit.current();
      /**
       * **The card is saved at Stripe, and not yet recorded here.**
       *
       * `setup_intent.succeeded` has to reach the webhook, be recorded, and come back
       * through the outbox and the bus before `list` shows it — which is normally a
       * second or two and occasionally longer. Closing straight away would show the
       * buyer a screen still saying they have no card, seconds after they saved one.
       */
      setState('confirming');
      await waitForCard();
      /**
       * **The other half of rule 4a, and the reason the wait above is not enough.**
       *
       * `waitForCard` only proves Orders knows about the card. Bidding reads
       * `hasCardOnFile` off the access token, and the token in hand was minted before
       * the card existed — so without this the buyer's first bid is still refused, now
       * for an hour rather than for a minute. The fact is client-caused, so the client
       * that caused it refreshes it.
       *
       * Not fatal if it fails: the pushed read model still catches up within the minute,
       * which is the behavior this replaces rather than depends on.
       */
      await refreshSession().catch(() => false);
      onSaved();
    } catch (error) {
      setProblem(describeError(error));
      setState('ready');
    }
  }

  return (
    <Sheet onDismiss={onDismiss}>
      <Stack gap="md">
        <Text as="h3" step="heading">
          Add a card
        </Text>
        {state === 'preparing' ? <Loading what="the card form" /> : null}
        <div ref={mountPoint} />
        {problem ? <Text tone="danger">{problem.message}</Text> : null}
        {state === 'confirming' ? (
          <Text step="label" tone="fg.muted">
            Saved — confirming with your bank…
          </Text>
        ) : null}
        <Stack direction="row" gap="sm" justify="end">
          <Pressable variant="secondary" onPress={onDismiss}>
            Cancel
          </Pressable>
          <Pressable
            onPress={() => void save()}
            busy={state === 'saving' || state === 'confirming'}
            disabled={state !== 'ready'}
          >
            Save card
          </Pressable>
        </Stack>
      </Stack>
    </Sheet>
  );
}

/**
 * Poll until the webhook's record lands, then give up gracefully.
 *
 * Bounded on purpose: if the fact is slow, the card still exists and the next load of
 * this screen will show it. Blocking the buyer behind an unbounded wait would turn a
 * delay into a hang, and the one thing worse than a slow confirmation is a stuck sheet.
 */
async function waitForCard(attempts = 6, delayMs = 700): Promise<void> {
  for (let attempt = 0; attempt < attempts; attempt++) {
    const { hasCardOnFile } = await api().query('orders.paymentmethod.list', {});
    if (hasCardOnFile) return;
    await new Promise((resolve) => setTimeout(resolve, delayMs));
  }
}
